import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import toast from 'react-hot-toast'
import { validatePassword, passwordRequirementsText } from '../utils/passwordPolicy'
import PasswordStrengthMeter from './PasswordStrengthMeter'

const AuthForm = () => {
  const [isSignUp, setIsSignUp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [verificationEmail, setVerificationEmail] = useState('')
  const [isResetMode, setIsResetMode] = useState(false)
  const [resetEmailSentTo, setResetEmailSentTo] = useState('')
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false)
  const [resetPasswords, setResetPasswords] = useState({ newPassword: '', confirmNewPassword: '' })
  const [linkError, setLinkError] = useState('')
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  })

  useEffect(() => {
    // Check if Supabase is properly configured
    if (!supabase || !supabase.auth) {
      const errorMsg = 'Supabase client not properly configured. Please check your setup.'
      setError(errorMsg)
      console.error(errorMsg)
    }
  }, [])

  useEffect(() => {
    // Listen for password recovery event after user clicks reset link
    if (!supabase || !supabase.auth || !supabase.auth.onAuthStateChange) return
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true)
        setIsResetMode(false)
        setVerificationEmail('')
        setIsSignUp(false)
        setError('')
      }
    })
    return () => subscription?.unsubscribe()
  }, [])

  useEffect(() => {
    // Clear any existing error when switching between sign in and sign up
    setError('')
  }, [isSignUp])

  useEffect(() => {
    // Handle Supabase error hash (e.g., expired/invalid password reset link)
    try {
      const { hash, pathname, search } = window.location
      if (hash && hash.length > 1) {
        const params = new URLSearchParams(hash.replace(/^#/, ''))
        const errorParam = params.get('error')
        const descriptionParamRaw = params.get('error_description')
        if (errorParam && descriptionParamRaw) {
          const description = decodeURIComponent(descriptionParamRaw.replace(/\+/g, ' '))
          setLinkError(description)
          setIsSignUp(false)
          setIsResetMode(false)
          setIsPasswordRecovery(false)
          setError('')
        }
        // Clear hash so the error is shown only once
        window.history.replaceState(null, '', `${pathname}${search}`)
      }
    } catch (_) {
      // noop
    }
  }, [])

  const handleInputChange = (e) => {
    setError('') // Clear error when user starts typing
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSignUp = async (e) => {
    e.preventDefault()
    setError('')
    
    if (formData.password !== formData.confirmPassword) {
      const errorMsg = 'Passwords do not match'
      setError(errorMsg)
      toast.error(errorMsg)
      return
    }

    const passwordPolicy = validatePassword(formData.password)
    if (!passwordPolicy.valid) {
      setError(passwordPolicy.message)
      toast.error(passwordPolicy.message)
      return
    }

    setLoading(true)
    
    try {
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
      })

      if (error) {
        console.error('Sign up error:', error)
        setError(error.message)
        toast.error(error.message)
      } else {
        setError('')
        console.log('Sign up successful:', data)
        if (!data?.session) {
          setVerificationEmail(formData.email)
          setFormData({ email: formData.email, password: '', confirmPassword: '' })
          toast.success('Check your email for a verification link')
        } else {
          toast.success('Account created!')
        }
      }
    } catch (error) {
      console.error('Unexpected sign up error:', error)
      const errorMsg = 'An unexpected error occurred during sign up'
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  const handleSignIn = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      })

      if (error) {
        console.error('Sign in error:', error)
        setError(error.message)
        toast.error(error.message)
      } else {
        setError('')
        console.log('Sign in successful:', data)
        toast.success('Welcome back!')
      }
    } catch (error) {
      console.error('Unexpected sign in error:', error)
      const errorMsg = 'An unexpected error occurred during sign in'
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`
        }
      })
      
      if (error) {
        toast.error(error.message)
      }
    } catch (error) {
      toast.error('Failed to sign in with Google')
    }
  }

  const handleGitHubSignIn = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: `${window.location.origin}/`
        }
      })
      
      if (error) {
        toast.error(error.message)
      }
    } catch (error) {
      toast.error('Failed to sign in with GitHub')
    }
  }

  const handleSendResetEmail = async (e) => {
    if (e) e.preventDefault()
    setError('')
    const targetEmail = resetEmailSentTo || formData.email
    if (!targetEmail) {
      setError('Please enter your email')
      return
    }
    setLoading(true)
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(targetEmail, {
        redirectTo: `${window.location.origin}/`
      })
      if (error) {
        toast.error(error.message)
        setError(error.message)
      } else {
        setResetEmailSentTo(targetEmail)
        toast.success('Password reset email sent')
      }
    } catch (error) {
      toast.error('Failed to send reset email')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdatePassword = async (e) => {
    e.preventDefault()
    setError('')
    if (resetPasswords.newPassword !== resetPasswords.confirmNewPassword) {
      const msg = 'Passwords do not match'
      setError(msg)
      toast.error(msg)
      return
    }

    const passwordPolicy = validatePassword(resetPasswords.newPassword)
    if (!passwordPolicy.valid) {
      setError(passwordPolicy.message)
      toast.error(passwordPolicy.message)
      return
    }
    setLoading(true)
    try {
      const { error } = await supabase.auth.updateUser({ password: resetPasswords.newPassword })
      if (error) {
        toast.error(error.message)
        setError(error.message)
      } else {
        toast.success('Password updated. You are now signed in.')
        setIsPasswordRecovery(false)
        setResetPasswords({ newPassword: '', confirmNewPassword: '' })
        setIsSignUp(false)
      }
    } catch (error) {
      toast.error('Failed to update password')
    } finally {
      setLoading(false)
    }
  }

  const handleResendVerification = async () => {
    if (!verificationEmail) return
    setLoading(true)
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: verificationEmail
      })
      if (error) {
        toast.error(error.message)
      } else {
        toast.success('Verification email resent')
      }
    } catch (error) {
      toast.error('Failed to resend verification email')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Welcome to MoonReady
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {isSignUp ? 'Create your account' : 'Sign in to your account'}
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        {verificationEmail ? (
          <div>
            <div className="text-center mb-6">
              <svg className="mx-auto h-12 w-12 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm4.243-12.657a1 1 0 10-1.486-1.336l-4.4 4.896-1.814-1.814a1 1 0 10-1.415 1.414l2.5 2.5a1 1 0 001.464-.04l5.151-5.62z"/>
              </svg>
              <h2 className="mt-4 text-2xl font-semibold text-gray-900 dark:text-white">Verify your email</h2>
              <p className="mt-2 text-gray-600 dark:text-gray-300">
                We sent a verification link to <span className="font-medium">{verificationEmail}</span>.
                Please check your inbox and spam folder.
              </p>
            </div>
            <div className="space-y-3">
              <button
                onClick={handleResendVerification}
                className="w-full py-2 px-4 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Resend verification email
              </button>
              <button
                onClick={() => setVerificationEmail('')}
                className="w-full py-2 px-4 rounded-md text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-gray-700 hover:bg-blue-100 dark:hover:bg-gray-600"
              >
                Use a different email
              </button>
              <button
                onClick={() => { setVerificationEmail(''); setIsSignUp(false); setError('') }}
                className="w-full py-2 px-4 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Go to sign in
              </button>
            </div>
          </div>
        ) : isPasswordRecovery ? (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Set a new password</h2>
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            )}
            <form onSubmit={handleUpdatePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">New password</label>
                <input
                  type="password"
                  value={resetPasswords.newPassword}
                  onChange={(e) => setResetPasswords({ ...resetPasswords, newPassword: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm 
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter new password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Confirm new password</label>
                <input
                  type="password"
                  value={resetPasswords.confirmNewPassword}
                  onChange={(e) => setResetPasswords({ ...resetPasswords, confirmNewPassword: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm 
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Confirm new password"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                         bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                         disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating...' : 'Update password'}
              </button>
              <button
                type="button"
                onClick={() => { setIsPasswordRecovery(false); setError('') }}
                className="w-full mt-2 py-2 px-4 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
            </form>
          </div>
        ) : isResetMode ? (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Reset your password</h2>
            {resetEmailSentTo ? (
              <div>
                <p className="mb-4 text-gray-700 dark:text-gray-300">We sent a password reset link to <span className="font-medium">{resetEmailSentTo}</span>. Please check your email.</p>
                <div className="space-y-3">
                  <button
                    onClick={handleSendResetEmail}
                    className="w-full py-2 px-4 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Resend reset email
                  </button>
                  <button
                    onClick={() => { setResetEmailSentTo('') }}
                    className="w-full py-2 px-4 rounded-md text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-gray-700 hover:bg-blue-100 dark:hover:bg-gray-600"
                  >
                    Use a different email
                  </button>
                  <button
                    onClick={() => { setIsResetMode(false); setError('') }}
                    className="w-full py-2 px-4 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    Back to sign in
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSendResetEmail} className="space-y-4">
                {error && (
                  <div className="mb-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                    <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm 
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                             focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                           bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                           disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                </button>
                <button
                  type="button"
                  onClick={() => { setIsResetMode(false); setError('') }}
                  className="w-full mt-2 py-2 px-4 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Back to sign in
                </button>
              </form>
            )}
          </div>
        ) : (<>
        {linkError && (
          <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
            <div className="text-sm text-yellow-800 dark:text-yellow-200">{linkError}</div>
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1-0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            </div>
          </div>
        )}
        <form onSubmit={isSignUp ? handleSignUp : handleSignIn} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm 
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm 
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your password"
            />
            {isSignUp && <PasswordStrengthMeter password={formData.password} />}
          </div>

          {isSignUp && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm 
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Confirm your password"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{passwordRequirementsText}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                     bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                     disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {isSignUp ? 'Creating account...' : 'Signing in...'}
              </div>
            ) : (
              isSignUp ? 'Create account' : 'Sign in'
            )}
          </button>
          {!isSignUp && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => { setIsResetMode(true); setError(''); setVerificationEmail('') }}
                className="mt-2 text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400"
              >
                Forgot password?
              </button>
            </div>
          )}
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">Or continue with</span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <button
              onClick={handleGoogleSignIn}
              className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm 
                       bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-300
                       hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="ml-2">Google</span>
            </button>

            <button
              onClick={handleGitHubSignIn}
              className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm 
                       bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-300
                       hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
              <span className="ml-2">GitHub</span>
            </button>
          </div>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => setIsSignUp(!isSignUp)}
            className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400"
          >
            {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
          </button>
        </div>
        </>) }
      </div>
    </div>
  )
}

export default AuthForm 